import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  CheckCircle2Icon,
  CheckIcon,
  Loader2Icon,
  MailCheckIcon,
  RefreshCwIcon,
} from "lucide-react";
import LOGO from "assets/logo.svg";
import LOGO_WHITE from "assets/logo-white.svg";
import { toast } from "utils/toast";

import {
  getFullVerificationToken,
  resendVerificationEmail,
  verifyAccount,
} from "api/accounts";

import { Button } from "js/components/ui/button";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";

import { signUpSteps } from "./SignUp";
import { userDataUpdated } from "../../../store/features/user/user";

interface VerifyFormData {
  shortCode: string;
}

interface RootState {
  entities: {
    user: {
      userData: {
        email: string;
        is_validated: boolean;
      } | null;
    };
  };
}

const VerifyAccount = () => {
  const navigate = useNavigate();
  const { userData } = useSelector((state: RootState) => state.entities.user);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const currentStep = 3; // Always on verification step

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VerifyFormData>({
    defaultValues: {
      shortCode: "",
    },
  });

  useEffect(() => {
    if (userData?.is_validated) {
      navigate("/");
    }
  }, [userData, navigate]);

  const handleResendEmail = async () => {
    setResending(true);
    try {
      await resendVerificationEmail();
      toast.success("Correo enviado. Revisa tu bandeja de entrada.");
    } catch (error: any) {
      console.error("Error send email", error);
      toast.error(
        error?.response?.data?.message || "Error al enviar el correo"
      );
    } finally {
      setResending(false);
    }
  };

  const getVerificationTokenFromShortCode = async (shortCode: string) => {
    try {
      const verificationTokenResponse =
        await getFullVerificationToken(shortCode);
      return verificationTokenResponse.data.verification_token;
    } catch (error: any) {
      console.error("Account verification failed", error);
      toast.error(
        error?.response?.data?.message || "Código de verificación inválido"
      );
      return null;
    }
  };

  const handleVerification = async (shortCode: string) => {
    const verification_token =
      await getVerificationTokenFromShortCode(shortCode);

    if (verification_token) {
      try {
        await verifyAccount(verification_token);
        const updatedUserData = { ...userData, is_validated: true };
        dispatch(userDataUpdated(updatedUserData));
        toast.success("Cuenta verificada exitosamente");
        navigate("/");
      } catch (error: any) {
        console.error(error);
        const message = error?.response?.data?.message;
        toast.error(message || "Error al verificar la cuenta");
        if (message === "Account already verified") {
          navigate("/");
        }
      }
    }
  };

  const onSubmit = async (formValues: VerifyFormData) => {
    setLoading(true);
    await handleVerification(formValues.shortCode.trim());
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen">
      {/* Left Section - Step Indicator */}
      <div
        className="relative hidden w-[380px] overflow-hidden lg:flex"
        style={{
          background:
            "linear-gradient(135deg, #0f0c4a 0%, #161467 50%, #1e1a7a 100%)",
        }}
      >
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient mesh */}
          <div
            className="absolute -top-40 -left-40 h-[700px] w-[700px] rounded-full opacity-50 blur-[100px]"
            style={{
              background:
                "radial-gradient(circle, rgba(124,58,237,0.6) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute -bottom-40 -right-40 h-[600px] w-[600px] rounded-full opacity-40 blur-[100px]"
            style={{
              background:
                "radial-gradient(circle, rgba(79,70,229,0.6) 0%, transparent 70%)",
            }}
          />

          {/* Subtle noise texture */}
          <div
            className="absolute inset-0 opacity-[0.25]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              mixBlendMode: "overlay",
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex w-full flex-col justify-between p-8 text-white xl:p-12">
          {/* Logo */}
          <a href="https://multiburo.com.mx/" className="self-start">
            <img
              src={LOGO_WHITE}
              alt="Multiburo"
              className="h-14 w-auto xl:h-16"
            />
          </a>

          {/* Step Indicator */}
          <div className="flex flex-1 flex-col justify-center py-8">
            <div className="space-y-6">
              {signUpSteps.map((step, index) => {
                const isCompleted = currentStep > step.id;
                const isCurrent = currentStep === step.id;

                return (
                  <div key={step.id} className="flex gap-4">
                    {/* Step indicator */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors duration-300 ${
                          isCompleted
                            ? "border-white bg-white text-primary"
                            : isCurrent
                              ? "border-white bg-white/20 text-white"
                              : "border-white/30 bg-transparent text-white/50"
                        }`}
                      >
                        {isCompleted ? (
                          <CheckIcon className="h-4 w-4" />
                        ) : (
                          <span className="text-sm font-semibold">
                            {step.id}
                          </span>
                        )}
                      </div>
                      {/* Connector line */}
                      {index < signUpSteps.length - 1 && (
                        <div
                          className="mt-2 h-12 w-0.5"
                          style={{
                            backgroundColor: isCompleted
                              ? "white"
                              : "rgba(255,255,255,0.3)",
                          }}
                        />
                      )}
                    </div>

                    {/* Step content */}
                    <div className="pt-1.5">
                      <h3
                        className={`font-semibold transition-colors duration-300 ${
                          isCurrent || isCompleted
                            ? "text-white"
                            : "text-white/50"
                        }`}
                      >
                        {step.title}
                      </h3>
                      <p className="mt-0.5 text-sm text-white/60">
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-sm text-white/40">
            {new Date().getFullYear()} Todos los derechos reservados
          </div>
        </div>
      </div>

      {/* Right Section - Form */}
      <div className="relative flex flex-1 flex-col justify-center px-6 py-12 sm:px-12 lg:px-16">
        {/* Mobile Progress Bar - Fixed at very top */}
        <div className="absolute left-0 right-0 top-0 h-1 bg-[#161467]/20 lg:hidden">
          <div
            className="h-full transition-all duration-500 ease-out"
            style={{
              width: `${(currentStep / signUpSteps.length) * 100}%`,
              background: "linear-gradient(135deg, #0f0c4a 0%, #161467 50%, #1e1a7a 100%)"
            }}
          />
        </div>

        <motion.div
          className="mx-auto w-full max-w-md space-y-8"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {/* Logo - visible only on mobile */}
          <div className="flex justify-start lg:hidden">
            <a href="https://multiburo.com.mx/">
              <img src={LOGO} alt="Multiburo" className="h-14 w-auto" />
            </a>
          </div>

          {/* Success Icon */}
          <div className="flex justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
              <MailCheckIcon className="h-10 w-10 text-primary" />
            </div>
          </div>

          {/* Header */}
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Verifica tu cuenta
            </h1>
            <p className="text-muted-foreground">
              Introduce el código de verificación de 6 dígitos que enviamos a{" "}
              <span className="font-medium text-foreground">
                {userData?.email}
              </span>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="shortCode">Código de verificación</Label>
              <Controller
                name="shortCode"
                control={control}
                rules={{
                  required: "El código es requerido",
                  minLength: {
                    value: 6,
                    message: "El código debe tener 6 caracteres",
                  },
                  maxLength: {
                    value: 6,
                    message: "El código debe tener 6 caracteres",
                  },
                }}
                render={({ field }) => (
                  <Input
                    id="shortCode"
                    placeholder="ABC123"
                    maxLength={6}
                    className={`h-12 text-center font-mono text-lg tracking-widest ${
                      errors.shortCode ? "border-destructive" : ""
                    }`}
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value.trim())
                    }
                  />
                )}
              />
              {errors.shortCode && (
                <p className="text-sm text-destructive">
                  {errors.shortCode.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="h-11 w-full text-base font-semibold"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                "Verificar cuenta"
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="h-11 w-full"
              onClick={handleResendEmail}
              disabled={resending}
            >
              {resending ? (
                <>
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <RefreshCwIcon className="mr-2 h-4 w-4" />
                  Reenviar código
                </>
              )}
            </Button>
          </form>

          {/* Help Text */}
          <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <p>
                ¿No recibiste el correo? Revisa tu carpeta de spam o solicita un
                nuevo código.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default VerifyAccount;
